import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'telefono',
  titolo: 'Il gestionale sul telefono',
  gruppo: 'Per iniziare',
  permesso: null,
  aCheServe: 'Il gestionale funziona anche dal telefono, nel browser o con l’app CIDIS. Qui trovi che cosa '
    + 'cambia rispetto al computer.',
  parti: [
    {
      titolo: 'Dal browser del telefono',
      passi: [
        'Apri nel browser lo stesso indirizzo che usi dal computer ed entra come sempre.',
        'Il menu è nascosto: lo apri con il comando in alto a sinistra («Apri la navigazione»). In cima al '
          + 'menu leggi il tuo nome e i tuoi ruoli.',
        'Nella linguetta «Documenti» di una scheda, «Fotografa il documento» apre direttamente la fotocamera.',
      ],
      immagine: {
        file: 'telefono.webp', larghezza: 780, altezza: 1520,
        descrizione: 'Il gestionale sullo schermo di un telefono, con il menu aperto.',
      },
    },
    {
      titolo: 'L’invito a installare l’app',
      testo: [
        'Sul cruscotto un riquadro ti invita a mettere il gestionale sul telefono: dal computer con un codice QR, '
          + 'da Android con il pulsante «Scarica l’app», dall’iPhone con i tre tocchi per la schermata Home. Sulle '
          + 'altre pagine, in cima, una riga ti ricorda la stessa cosa. Dentro l’app, se apri il gestionale dalla '
          + 'schermata Home, o se hai già un telefono connesso, l’invito non compare.',
      ],
      passi: [
        'Dal computer inquadra il codice QR del cruscotto con la fotocamera del telefono: si apre «Il mio '
          + 'profilo» al riquadro «App per il telefono». Se sul telefono non sei ancora entrato, prima entri come sempre.',
        'Da Android, «Scarica l’app» scarica subito l’app; «Istruzioni» apre il riquadro con i passi.',
        'La crocetta nasconde il riquadro del cruscotto per trenta giorni, e la riga in cima alle altre pagine '
          + 'per sette.',
      ],
    },
    {
      titolo: 'L’app CIDIS per Android',
      testo: [
        'L’app apre lo stesso gestionale, con in più lo sblocco del telefono e gli avvisi sullo schermo. Non è su '
          + 'Google Play: si scarica dal gestionale, in «Il mio profilo», riquadro «App per il telefono».',
      ],
      passi: [
        'Dal telefono apri «Il mio profilo» e tocca «Scarica l’app per Android»; finito lo scaricamento, apri il '
          + 'file CIDIS.apk.',
        'Se il telefono lo chiede, consenti al browser di installare app da questa fonte, poi «Installa».',
        'Il telefono deve avere un codice di sblocco: senza, l’app non si apre.',
        'All’apertura, e dopo cinque minuti in secondo piano, l’app chiede il volto, l’impronta o il codice '
          + 'del telefono.',
        'Dopo il primo accesso l’app resta connessa: per 30 giorni dall’ultimo uso si apre senza nome utente e '
          + 'password, con il solo sblocco del telefono.',
        'Al primo accesso l’app chiede il permesso di mandarti notifiche. Se lo dai, a ogni avviso nuovo il '
          + 'telefono mostra «Hai un nuovo avviso nel gestionale», senza dire di che cosa si tratta. Tocca la '
          + 'notifica: si apre la pagina «Notifiche».',
        'In fondo alla pagina «Notifiche», «Telefoni collegati» elenca i telefoni che ricevono i tuoi avvisi. '
          + '«Rimuovi» scollega un telefono perso o che non usi più.',
      ],
      nota: 'Con «Esci» il telefono si scollega dagli avvisi; quando rientri si ricollega.',
    },
    {
      titolo: 'Sull’iPhone: il gestionale nella schermata Home',
      testo: [
        'L’app per iPhone arriverà con l’App Store. Intanto il gestionale si aggiunge alla schermata Home e si '
          + 'apre a tutto schermo, come un’app. Con «Attiva Face ID» resta connesso: per 30 giorni dall’ultimo '
          + 'uso si apre con Face ID o con il codice dell’iPhone, senza password. Gli avvisi sullo schermo '
          + 'arriveranno con l’app.',
      ],
      passi: [
        'Apri il gestionale con Safari.',
        'Tocca il pulsante Condividi (il quadrato con la freccia verso l’alto).',
        'Scegli «Aggiungi alla schermata Home», poi «Aggiungi». La prima volta entri di nuovo con nome utente e '
          + 'password.',
        'In basso compare «Resta connesso su questo telefono»: tocca «Attiva Face ID» e conferma con Face ID. '
          + '«Non ora» lo ripropone al prossimo accesso.',
        'Da lì il gestionale si apre con «Sblocca con Face ID». Dopo cinque minuti in secondo piano lo schermo '
          + 'si copre e chiede di nuovo Face ID. Se Face ID non riesce, l’iPhone chiede il suo codice; «Entra con '
          + 'nome utente e password» resta sempre possibile.',
      ],
    },
    {
      titolo: 'Telefoni connessi',
      testo: [
        'In «Il mio profilo», «Telefoni connessi» elenca i telefoni che entrano senza password: l’app Android e '
          + 'l’iPhone con Face ID, con il giorno dell’ultimo uso e la scadenza. Un telefono fermo per 30 giorni '
          + 'chiede di nuovo nome utente, password e secondo fattore.',
      ],
      passi: [
        '«Scollega» toglie un telefono perso o che non usi più, e chiude le sessioni aperte da quel telefono.',
        '«Esci» dal telefono lo scollega: la volta dopo entri di nuovo con nome utente e password.',
        'Se cambi la password, gli altri telefoni si scollegano; resta quello da cui la cambi.',
      ],
    },
    {
      titolo: 'L’agenda e i calendari sul telefono',
      permesso: ['agenda.leggi', 'mediazione.propria'],
      testo: [
        'Sotto i 600 px di larghezza l’agenda e i calendari mostrano l’elenco del giorno: ‹ e › cambiano giorno, '
          + '«Oggi» torna a oggi, «+» fissa un impegno. La settimana diventa un elenco diviso per giorno. Turni e '
          + 'prenotazioni dal telefono non si trascinano: si aprono e si modificano.',
      ],
    },
    {
      titolo: 'Il calendario in Outlook e sul telefono',
      permesso: ['agenda.leggi', 'mediazione.propria'],
      testo: [
        'I tuoi impegni possono stare anche nel calendario del telefono, di Outlook o di Google. Escono giorno, ora, '
          + 'che cosa («Turno in sede», «Mediazione CIDIS (arabo)», «Appuntamento in sede») e la sede con l’indirizzo: '
          + 'mai i nomi delle persone, i titoli e le note.',
        'Il file .ics si scarica: «Aggiungi al calendario (.ics)» nella mediazione, «Scarica il file per il calendario '
          + '(.ics)» in «Il mio calendario». Se un impegno cambia va scaricato di nuovo.',
        'Il collegamento si aggiorna da solo, ma c’è solo se CIDIS ha acceso il calendario in abbonamento: in «Il mio '
          + 'profilo» compare il riquadro «Il calendario in Outlook e sul telefono».',
      ],
      passi: [
        'In «Il mio profilo», riquadro «Il calendario in Outlook e sul telefono», scegli il «Calendario» («La mia '
          + 'agenda» o «Le mie mediazioni») e un «Nome» per riconoscerlo, per esempio «iPhone».',
        'Premi «Crea il collegamento». Il collegamento si vede solo adesso: «Copia», oppure dal telefono «Apri sul '
          + 'telefono».',
        'Outlook: Calendario → Aggiungi calendario → Da Internet, incolla il collegamento. Google Calendar: Altri '
          + 'calendari → + → Da URL. iPhone: «Apri sul telefono» e «Abbonati». Android: aggiungilo da Google Calendar '
          + 'sul computer.',
        'L’elenco del riquadro dice quando ogni collegamento è stato usato l’ultima volta e da quale rete. Se non lo '
          + 'riconosci, o se perdi il telefono, premi «Revoca».',
      ],
      nota: 'Chi ha il collegamento vede i tuoi impegni: non condividerlo. Al più tre collegamenti; ciascuno vale 12 '
        + 'mesi, e se nessun programma lo usa per 90 giorni si revoca da solo. In palestra non se ne creano.',
    },
    {
      titolo: 'Il messenger sul telefono',
      permesso: 'chat.usa',
      testo: [
        'Per il messenger sul telefono usa l’app **Element Classic**, non Element X: con l’accesso del gestionale '
          + 'Element X non entra. I passi per iPhone e per Android sono nel riquadro «App per il telefono», sezione '
          + '«Messenger», e in cima alla pagina «Messenger» quando la apri dal telefono. Li trovi anche nel capitolo '
          + 'sul messenger.',
      ],
      nota: 'Dentro l’app CIDIS il riquadro «App per il telefono» mostra solo la sezione «Messenger».',
    },
    {
      titolo: 'Il telefono è uno strumento di lavoro',
      testo: [
        'Blocca il telefono con un codice e non lasciarlo incustodito. Se lo perdi, avvisa subito chi gestisce '
          + 'gli utenti e, da un computer, scollegalo da «Il mio profilo» → «Telefoni connessi» e toglilo da '
          + '«Telefoni collegati» nella pagina «Notifiche».',
      ],
    },
  ],
  avvertenze: [
    {
      quando: 'L’app non si apre.',
      cosaFare: 'Controlla che il telefono abbia un codice di sblocco: senza, l’app resta chiusa.',
    },
    {
      quando: 'Il calendario del telefono non mostra più gli impegni.',
      cosaFare: 'Il collegamento è scaduto, è stato revocato, oppure il calendario in abbonamento è stato spento. '
        + 'In «Il mio profilo» controlla il riquadro e, se c’è, crea un collegamento nuovo.',
    },
    {
      quando: 'Non ricevi gli avvisi sul telefono.',
      cosaFare: 'Controlla nelle impostazioni del telefono che le notifiche dell’app CIDIS siano accese, e che il '
        + 'telefono compaia in «Telefoni collegati».',
    },
  ],
  vediAnche: ['messenger', 'cruscotto', 'agenda', 'mediazioni'],
  parole: ['cellulare', 'smartphone', 'app', 'iphone', 'android', 'push', 'notifiche', 'fotocamera', 'apk',
    'scarica', 'schermata home', 'installa', 'codice qr', 'più tardi', 'element classic', 'face id',
    'resta connesso', 'telefoni connessi', 'scollega', 'calendario', 'ics', 'outlook', 'google calendar',
    'abbonamento', 'agenda'],
}

export default capitolo
